import React from 'react';
import {IonBackButton, IonButtons, IonContent, IonFooter, IonHeader, IonModal, IonPage, IonTitle, IonToolbar, createGesture} from '@ionic/react';
import {ApiService} from "../api/ApiService";
import { HighwayCard } from '../components/HighwayCard';
import {UserState, userStore} from "../api/AuthService";
import {FuelEfficiencyByUnit} from "../api/model/FuelEfficiencyByUnit";
import Nav from './Nav';
import Toolbar from './Toolbar';
import { sidnavStore, SidenavState } from '../api/SidenavStore';
import { useHistory } from 'react-router-dom';

const actionButtonStyle: React.CSSProperties = {
    borderRadius: '25px',
    whiteSpace: 'normal',
    padding: '12px 28px',
    lineHeight: 1.2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    background: 'rgb(229, 244, 224)',
    border: 'none',
    color: '#000',
    fontWeight: 700,
    width: '100%',
    maxWidth: '260px',
    margin: '0 auto',
};


const Bullet: React.FC<{ text: string }> = ({text}) => (
    <li style={{marginBottom: 8}}>{text}</li>
);

const getDriverMpgColor = (driver: number, avg: number): string => {
    const delta = driver - avg;
    if (delta <= -0.2) return '#d40000'; // below -0.2
    if (delta < 0.2) return '#f1e408ff';  // within [-0.2, +0.2)
    return '#1B5E20';                   // >= +0.2
};

const mapFuelEfficiencyToMileageData = (data: FuelEfficiencyByUnit) => ({
    rankMpgAct: data.RANK_MPG_ACT,
    groupCount: data.GROUP_COUNT,
    rankMpgActPerc: data.RANK_MPG_ACT_PERC * 100,
    actuMpg: data.ACTU_MPG,
    moviMpg: data.MOVI_MPG,
    groupAveMoviMpg: data.GROUP_AVE_MOVI_MPG,
    totalIdleFuelUsed: data.TOTAL_IDLE_FUEL_USED,
    rankGalIdle: data.RANK_GAL_IDL,
    groupAveIdleGal: data.GROUP_AVE_IDLE_GAL,
});

const formatToTwoDecimals = (value: number) => value.toFixed(2);

const Mileage: React.FC = () => {
    const [showIdle, setShowIdle] = React.useState(false);
    const [showMoving, setShowMoving] = React.useState(false);
    const [fuelEfficiencyData, setFuelEfficiencyData] = React.useState<FuelEfficiencyByUnit | null>(null);
    const {unit}: { unit?: string } = userStore((state: UserState) => ({unit: state.driver?.unit}));

    const ref = React.useRef<HTMLInputElement>(null)

    React.useEffect(() => {
        if (!unit) {
            return;
        }
        gestureInit();
        ApiService.getFuelEfficiencyByUnit('mileage', {unitNumber: unit}).then(data => {
            console.log('fuelEfficiencyByUnit data', data);
            setFuelEfficiencyData(data);
        });
    }, []);

    const mileageData = fuelEfficiencyData ? mapFuelEfficiencyToMileageData(fuelEfficiencyData) : null;

    function formatPercentage(percentage: number) {
        const rounded = Math.round(100 - percentage);

        const mod100 = rounded % 100;
        if (mod100 >= 11 && mod100 <= 13) return `${rounded}th`;

        switch (rounded % 10) {
            case 1:
                return `${rounded}st`;
            case 2:
                return `${rounded}nd`;
            case 3:
                return `${rounded}rd`;
            default:
                return `${rounded}th`;
        }
    }

    const {open, close}: { open: boolean, close: () => void } = sidnavStore((state: SidenavState) => ({open: state.open, close: state.close}));
    const {toggleSidenav}: { toggleSidenav: () => void } = sidnavStore((state: SidenavState) => ({toggleSidenav: state.toggle}));

    const gestureInit = () => {
        const card = ref.current
        if (card) {
            const gesture = createGesture({
                el: card,
                gestureName: 'swipeMenu',
                onMove: (detail) => {
                    console.log("moving");
                },
                onEnd: (detail) => {
                    console.log("move ended");
                    toggleSidenav();
                }                 
            })
            gesture.enable();
        }
    }

    function buildYouTubeEmbedUrl(videoId: string) {
        return `https://westernexpressmobile.com/v2/manual/youtube.html?v=${videoId}&autoplay=1&origin=https://westernexpressmobile.com`;
    }

    let history = useHistory();

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar color="primary">
                    <Toolbar pageName="Trip"/>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen color="light">
                {!mileageData ? (
                    <div style={{background: 'white', borderRadius: '24px', color: 'black', marginBottom: '25px'}} className="p-4 text-center">
                        No mileage data available.
                    </div>
                ) : (
                <div style={{background: 'transparent', borderRadius: '12px', color: 'black', fontFamily: 'Inria Sans'}} className="m-3 d-flex flex-column gap-3 pb-3">
                    <div className="d-flex flex-column align-items-center justify-content-center text-center" style={{fontSize: '15px'}}>
                        <HighwayCard
                            percentile={mileageData.rankMpgActPerc}
                            mpg={mileageData.actuMpg}
                            fleetRank={mileageData.rankMpgAct}
                            groupCount={mileageData.groupCount}
                            avgMpg={mileageData.groupAveMoviMpg}
                        />
                    </div>
                    <div style={{textAlign: 'center', margin: '8px 0', color: '#6b6b6b'}}>
                        <div>How do we find MPG?</div>
                        <div>1) Moving MPG: gallons burned while driving</div>
                        <div>2) Idle time</div>
                    </div>
                    {/* Combined stats card */}
                    <div style={{background: '#fff', borderRadius: '24px', margin: '0 4px', fontFamily: 'Inria Sans', overflow: 'hidden'}} id="mileage-stats">

                        {/* Row 1: Your Driving MPG */}
                        <div style={{display: 'flex', justifyContent: 'space-around', alignItems: 'center', padding: '14px 20px'}}>
                            <span style={{fontSize: '18px', fontWeight: 700}}>Your Driving MPG</span>
                            <span style={{fontSize: '24px', fontWeight: 700, color: getDriverMpgColor(mileageData.moviMpg, mileageData.groupAveMoviMpg)}}>{formatToTwoDecimals(mileageData.moviMpg)}</span>
                        </div>
                        <div style={{borderTop: '1px solid #ececec', margin: '0 16px'}} />

                        {/* Row 2: Fleet Rank + AVG Fleet MPG side by side */}
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1px 1fr', padding: '14px 0'}}>
                            <div style={{textAlign: 'center', padding: '0 16px'}}>
                                <div style={{fontSize: '14px', color: '#6b6b6b', marginBottom: '4px'}}><strong>Fleet Rank</strong></div>
                                <div style={{fontSize: '22px', fontWeight: 700}}>{mileageData.rankMpgAct}/{mileageData.groupCount}</div>
                            </div>
                            <div style={{background: '#ececec'}} />
                            <div style={{textAlign: 'center', padding: '0 16px'}}>
                                <div style={{fontSize: '14px', color: '#6b6b6b', marginBottom: '4px'}}><strong>AVG Fleet MPG</strong></div>
                                <div style={{fontSize: '22px', fontWeight: 700}}>{formatToTwoDecimals(mileageData.groupAveMoviMpg)}</div>
                            </div>
                        </div>
                        <div style={{borderTop: '1px solid #ececec', margin: '0 16px'}} />

                        {/* Row 3: Your Idle Gallons */}
                        <div style={{display: 'flex', justifyContent: 'space-around', alignItems: 'center', padding: '14px 20px'}}>
                            <span style={{fontSize: '18px', fontWeight: 700}}>Your Idle Gallons</span>
                            <span style={{fontSize: '24px', fontWeight: 700}}>{formatToTwoDecimals(mileageData.totalIdleFuelUsed)}</span>
                        </div>
                        <div style={{borderTop: '1px solid #ececec', margin: '0 16px'}} />

                        {/* Row 4: Fleet Idle Rank + AVG Fleet Idle side by side */}
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1px 1fr', padding: '14px 0'}}>
                            <div style={{textAlign: 'center', padding: '0 16px'}}>
                                <div style={{fontSize: '14px', color: '#6b6b6b', marginBottom: '4px'}}><strong>Fleet Idle Rank</strong></div>
                                <div style={{fontSize: '22px', fontWeight: 700}}>{mileageData.rankGalIdle}/{mileageData.groupCount}</div>
                            </div>
                            <div style={{background: '#ececec'}} />
                            <div style={{textAlign: 'center', padding: '0 16px'}}>
                                <div style={{fontSize: '14px', color: '#6b6b6b', marginBottom: '4px'}}><strong>AVG Fleet Idle</strong></div>
                                <div style={{fontSize: '22px', fontWeight: 700}}>{formatToTwoDecimals(mileageData.groupAveIdleGal)}</div>
                            </div>
                        </div>

                    </div>

                    {/* Action buttons */}
                    <div id="improve-fuel-efficiency-buttons" className="px-2 d-flex flex-column align-items-center" style={{width: '100%', textAlign: 'center', gap: '32px'}}>
                        <button style={actionButtonStyle} onClick={() => setShowIdle(true)}>
                            Improving Idle <span style={{fontSize: '18px', fontWeight: 700}}>›</span>
                        </button>
                        <button style={actionButtonStyle} onClick={() => setShowMoving(true)}>
                            Improving Moving MPG <span style={{fontSize: '18px', fontWeight: 700}}>›</span>
                        </button>
                        <button style={actionButtonStyle} onClick={() => history.push('/fuelDocs')}>
                            Fuel Docs <span style={{fontSize: '18px', fontWeight: 700}}>›</span>
                        </button>
                    </div>
                </div>
                )}
                <IonModal isOpen={showIdle} onDidDismiss={() => setShowIdle(false)}>
                    <div style={{padding: 16, color: 'black', overflowY: "scroll"}}>
                        <div style={{background: '#E5F4E0', padding: 12, borderRadius: 16, textAlign: 'center', fontWeight: 800, marginBottom: 12}}>How to improve idle time?</div>
                        <iframe title={fuelEfficiencyData?.TRUCK_MAKE === 'FRHT' ? "How to use Optimize Idle in a Freightliner" : "How to use Auto Start/Stop System in an International"} width="100%" height="345" src={ fuelEfficiencyData?.TRUCK_MAKE === 'FRHT' ? buildYouTubeEmbedUrl('yFJCH_9QXMQ') : (buildYouTubeEmbedUrl('EZe_K-yp9kU'))}></iframe>
                        <div style={{background: '#f7f7f7', borderRadius: 16, padding: 14}}>
                            <p style={{marginTop: 0}}>Excessive idling doesn't just waste fuel - it wears down your engine, increases maintenance costs, and cuts into your take- home pay. Whether
                                you're parked for a break, waiting at a dock, or powering your cab overnight, there are smarter ways to manage downtime and keep your truck running efficiently. Here's
                                how you can reduce idle time and save more with every mile:
                            </p>
                            <ul style={{paddingLeft: 20}}>
                                <Bullet text="Always turn your truck off when you exit it."/>
                                <Bullet text="Turn the truck off if there will be an extended wait time at the shipper."/>
                                <Bullet text="Turn off the truck while loading and unloading."/>
                                <Bullet text="Report any truck issues to the shop ASAP."/>
                            </ul>
                            <div style={{textAlign: 'center', marginTop: 12}}>
                                <button className="app-btn app-btn-primary" onClick={() => setShowIdle(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                </IonModal>

                <IonModal isOpen={showMoving} onDidDismiss={() => setShowMoving(false)}>
                    <div style={{padding: 16, color: 'black', overflowY: "scroll"}}>
                        <div style={{background: '#E5F4E0', padding: 12, borderRadius: 16, textAlign: 'center', fontWeight: 800, marginBottom: 12}}>How to improve moving MPG?</div>
                        <div style={{background: '#f7f7f7', borderRadius: 16, padding: 14}}>
                            <p style={{marginTop: 0}}>
                                You might not be paying for the fuel-but how you drive still makes a big difference in how your truck runs and how many miles you can cover. Running more efficiently
                                means fewer breakdowns, fewer shop visits, and more chances to stay moving (and earning). Dispatch notices who's running clean-and so does your equipment.
                                Here's how to keep your truck running smooth and your wheels turning:
                            </p>
                            <ul style={{paddingLeft: 20}}>
                                <Bullet text="Always set your cruise control when driving on the interstate."/>
                                <Bullet text="Maintain good tire pressure."/>
                                <Bullet text="Minimize braking."/>
                                <Bullet text="If your truck doesn't have cab extenders, request them from the shop."/>
                            </ul>
                            <div style={{textAlign: 'center', marginTop: 12}}>
                                <button className="app-btn app-btn-primary" onClick={() => setShowMoving(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                </IonModal>
            </IonContent>
            <IonFooter>
                <Nav pageName="Mileage" onSearch={function (target: boolean): void {
                    console.log("Not ready");
                } }/>
            </IonFooter>
        </IonPage>
    );
};

export default Mileage;
